import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { BrnService } from '../../services/brn.service';

interface ChatChip {
  label: string;
  state: 'active' | 'inactive';
}

@Component({
  selector: 'app-chat',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './chat.component.html',
  styleUrls: ['./chat.component.scss']
})
export class ChatComponent implements OnInit {
  chips: ChatChip[] = [
    { label: 'Klaviyo', state: 'active' },
    { label: 'Shopify', state: 'active' },
    { label: 'Meta', state: 'inactive' },
    { label: 'Google Ads', state: 'inactive' }
  ];

  promptText = '';
  isSending = false;

  readonly baseIcon = 'assets/chat/icon-past-conversations-base.svg';
  readonly midIcon = 'assets/chat/icon-past-conversations-mid.svg';
  readonly topIcon = 'assets/chat/icon-past-conversations-top.svg';

  private botId: string | null = null;
  private widgetClient: { userId: string; clientId: string } | null = null;
  private hasTriggeredFirstQuestionRequests = false;

  constructor(
    private readonly route: ActivatedRoute, 
    private readonly router: Router,
    private readonly brnService: BrnService
  ) {}

  ngOnInit(): void {
    this.botId = this.route.snapshot.paramMap.get('bot_id');
    if (!this.botId) {
      console.error('No bot_id found in route parameters');
    }
    // Preload Faye client script early to reduce WS connect latency
    this.preloadFayeScript();
  }

  toggleChip(chip: ChatChip): void {
    chip.state = chip.state === 'active' ? 'inactive' : 'active';
  }

  async sendPrompt(): Promise<void> {
    const text = this.promptText.trim();
    if (!text || this.isSending) {
      return;
    }

    const botId = this.resolveBotId();
    if (!botId) {
      console.error('Unable to send prompt: missing bot context.');
      alert('Bot ID is missing. Please select a store first.');
      return;
    }

    console.log('[Chat] sendPrompt start', { botId, hasTriggeredFirstQuestionRequests: this.hasTriggeredFirstQuestionRequests });
    this.isSending = true;

    try {
      console.log('[Chat] 1. Ensuring widget connection...');
      await this.ensureWidgetConnection(botId);
      console.log('[Chat] 1. Widget connection ensured');

      console.log('[Chat] 2. Ensuring widget client...');
      const client = await this.ensureWidgetClient(botId);
      console.log('[Chat] 2. Widget client ready', client);

      // Ensure Faye script is present before navigating
      this.preloadFayeScript();

      // Persist first question context for history screen to consume (non-blocking)
      if (!this.hasTriggeredFirstQuestionRequests) {
        try {
          const storageKey = this.buildFirstQuestionStorageKey(botId, client.userId);
          const payload = {
            botId,
            userId: client.userId,
            clientId: client.clientId,
            question: text,
            sentAt: Date.now()
          };
          sessionStorage.setItem(storageKey, JSON.stringify(payload));
          console.log('[Chat] stored first question payload', { storageKey, payload });
        } catch {}
      }

      const messageText = this.buildPromptMessage(text);
      console.log('[Chat] 3. Sending message', { messageText });

      const response = await firstValueFrom(
        this.brnService.sendWidgetMessage({
          bot_id: botId,
          user_id: client.userId,
          type: 'text',
          content: {
            text: messageText
          }
        })
      );

      console.log('[Chat] 4. Message sent successfully', response);
      this.promptText = '';
      
      // Note: first-question subject detection is now handled in chat history component
      
      // Redirect to message history page after successful send
      if (this.botId && client.userId) {
        this.router.navigate(['/chat', this.botId, client.userId]);
      }
    } catch (error) {
      console.error('[Chat] Failed to send prompt', error);
      alert('Failed to send message: ' + (error as any)?.message || 'Unknown error');
    } finally {
      this.isSending = false;
      console.log('[Chat] sendPrompt finished');
    }
  }

  private resolveBotId(): string | null {
    return this.botId ?? localStorage.getItem('current-bot-id');
  }

  private async ensureWidgetConnection(botId: string): Promise<void> {
    try {
      const connections = await firstValueFrom(this.brnService.getWidgetConnections(botId));
      const hasWidget = connections.connections?.some(conn => conn.provider === 'widget');

      if (!hasWidget) {
        await firstValueFrom(this.brnService.createWidgetConnection(botId, {}));
      }
    } catch (error) {
      console.error('Failed to ensure widget connection', error);
      throw error;
    }
  }

  private async ensureWidgetClient(botId: string): Promise<{ userId: string; clientId: string }> {
    if (this.widgetClient) {
      return this.widgetClient;
    }

    try {
      const clients = await firstValueFrom(this.brnService.getWidgetClients(botId));
      const existing = clients.find(client => {
        const data = client.provider_data as Record<string, unknown>;
        return typeof data?.['user_id'] === 'string';
      });

      if (existing) {
        const userId = (existing.provider_data as Record<string, unknown>)['user_id'] as string;
        if (userId) {
          this.widgetClient = {
            clientId: existing.client_id,
            userId
          };
          return this.widgetClient;
        }
      }
    } catch (error) {
      console.warn('Unable to load existing widget clients', error);
    }

    const userId = this.generateUserId();
    const name = `Dashboard Chat ${new Date().toLocaleDateString()}`;

    const created = await firstValueFrom(
      this.brnService.createWidgetClient({
        name,
        bot_id: botId,
        provider_data: {
          user_id: userId
        }
      })
    );

    this.widgetClient = {
      clientId: created.client.client_id,
      userId
    };

    return this.widgetClient;
  }

  private buildPromptMessage(baseText: string): string {
    /*
    const activeChannels = this.chips
      .filter(chip => chip.state === 'active')
      .map(chip => chip.label);

    if (activeChannels.length) {
      return `${baseText} (Channels: ${activeChannels.join(', ')})`;
    }
      */

    return baseText;
  }

  private generateUserId(): string {
    if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
      return crypto.randomUUID();
    }

    return 'widget-' + Math.random().toString(36).slice(2, 10);
  }

  private buildFirstQuestionStorageKey(botId: string, userId: string): string {
    return `stitch:first-question:${botId}:${userId}`;
  }

  private preloadFayeScript(): void {
    try {
      if ((window as any).Faye?.Client) return; // already loaded
      const existing = document.getElementById('faye-client-script');
      if (existing) return;
      console.log('[Chat] Preloading Faye script');
      const script = document.createElement('script');
      script.id = 'faye-client-script';
      script.type = 'text/javascript';
      script.src = 'https://faye.brn.ai/faye/client.js';
      script.async = true;
      document.head.appendChild(script);
    } catch {}
  }

  private async triggerFirstQuestionRequests(botId: string, firstQuestion: string): Promise<void> {
    try {
      const servicesResp = await firstValueFrom(
        this.brnService.getServicesByCapability(botId, 'response_generation')
      );
      const installed = servicesResp?.services?.find(s => !!s.installed);
      if (installed) {
        console.log('Installed response_generation service:', installed.name);
        // Ask provider to generate a short conversation subject
        const subjectResp = await firstValueFrom(
          this.brnService.generateServiceResponse(botId, installed.name, firstQuestion)
        );
        const subject =
          (subjectResp as any)?.text ||
          (subjectResp as any)?.output ||
          (subjectResp as any)?.message ||
          (typeof (subjectResp as any)?.response === 'string' ? (subjectResp as any).response : undefined);
        if (subject) {
          console.log('CHAT SUBJECT:', subject);
          try {
            await firstValueFrom(
              this.brnService.updateClient(this.widgetClient!.clientId, botId, subject)
            );
            console.log('Updated client name with CHAT SUBJECT');
          } catch (e) {
            console.warn('Failed to update client name', e);
          }
        } else {
          console.log('CHAT SUBJECT: <empty response>');
        }
      } else {
        console.log('No installed response_generation service found');
      }
    } catch (error) {
      console.warn('Failed to check services by capability', error);
    }
  }
}

